import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Monitor, GraduationCap, Briefcase, Lightbulb, Users, BookOpen, Award, Target, ChevronRight, Linkedin } from "lucide-react";
import axios from "axios";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Monitor, GraduationCap, Briefcase, Lightbulb,
};

function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const increment = target / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
}

function ImpactNumber({ value, label, suffix = "" }) {
  const { count, ref } = useCountUp(value);
  return (
    <div ref={ref} className="text-center" data-testid={`impact-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="font-body text-4xl md:text-5xl font-bold text-slate-50 tracking-tighter mb-2">
        {count}{suffix}
      </div>
      <div className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400">
        {label}
      </div>
    </div>
  );
}

export default function HomePage() {
  const [programs, setPrograms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/programs`).then((r) => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/projects?limit=3`).then((r) => setProjects(r.data)).catch(() => {});
    axios.get(`${API}/blog?limit=3`).then((r) => setBlogPosts(r.data)).catch(() => {});
    axios.get(`${API}/partners`).then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1485740112426-0c2549fa8c86?w=1920&q=80)`,
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24">
          <div className="max-w-3xl">
            <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-6 opacity-0 animate-fade-in-up">
              EDUCPEOPLE.ORG
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-slate-50 mb-8 opacity-0 animate-fade-in-up animate-stagger-1 leading-[1.1]">
              L'{"\u00e9"}ducation et l'IA au service de l'{"\u00e9"}galit{"\u00e9"} des chances
            </h1>
            <p className="font-body text-base md:text-lg font-light leading-relaxed text-slate-300 mb-10 max-w-2xl opacity-0 animate-fade-in-up animate-stagger-2">
              EDUCPEOPLE accompagne les publics vuln{"\u00e9"}rables vers la r{"\u00e9"}ussite {"\u00e9"}ducative, l'inclusion num{"\u00e9"}rique et l'employabilit{"\u00e9"}.
            </p>
            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in-up animate-stagger-3">
              <Link
                to="/projets"
                data-testid="hero-cta-actions"
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-full font-body font-medium transition-all duration-300"
              >
                D{"\u00e9"}couvrir nos actions
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/soutenir"
                data-testid="hero-cta-support"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-full font-body font-medium transition-all duration-300"
              >
                Soutenir l'association
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Numbers */}
      <section className="py-24 md:py-32 bg-slate-950" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <ImpactNumber value={500} suffix="+" label={`B\u00e9n\u00e9ficiaires`} />
            <ImpactNumber value={30} label="Ateliers" />
            <ImpactNumber value={12} label="Partenaires" />
            <ImpactNumber value={3} label="Programmes actifs" />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Programs */}
      <section className="py-24 md:py-32" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-4">
              Nos Programmes
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-slate-50">
              Former, Inclure, {"\u00c9"}manciper
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {programs.map((prog, i) => {
              const IconComp = ICON_MAP[prog.icon] || Target;
              return (
                <Link
                  key={prog.slug}
                  to={`/programmes/${prog.slug}`}
                  data-testid={`program-card-${prog.slug}`}
                  className={`group relative bg-slate-900/50 border border-white/10 rounded-3xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 program-card-hover ${
                    i === 0 ? "md:row-span-2" : ""
                  }`}
                >
                  <div className="relative h-48 md:h-full min-h-[200px] overflow-hidden">
                    <img
                      src={prog.image_url}
                      alt={prog.title}
                      className="w-full h-full object-cover img-zoom opacity-60 group-hover:opacity-80 transition-opacity duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                        <IconComp className="w-5 h-5 text-blue-400" />
                      </div>
                    </div>
                    <h3 className="font-heading text-2xl md:text-3xl font-light text-slate-50 mb-2">
                      {prog.title}
                    </h3>
                    <p className="font-body text-sm text-slate-400 line-clamp-2">
                      {prog.description}
                    </p>
                    <div className="flex items-center gap-2 mt-4 text-blue-400 font-body text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      En savoir plus <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Featured Projects */}
      <section className="py-24 md:py-32" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-4">
                Nos Actions
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-slate-50">
                Projets phares
              </h2>
            </div>
            <Link
              to="/projets"
              data-testid="see-all-projects"
              className="hidden md:inline-flex items-center gap-2 text-blue-400 font-body text-sm font-medium hover:text-blue-300 transition-colors"
            >
              Voir tous les projets <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.slice(0, 3).map((project) => (
              <Link
                key={project.slug}
                to={`/projets/${project.slug}`}
                data-testid={`project-card-${project.slug}`}
                className="group bg-slate-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 program-card-hover"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-full object-cover img-zoom opacity-70 group-hover:opacity-90 transition-opacity"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-medium text-slate-50 mb-2 group-hover:text-blue-400 transition-colors">
                    {project.title}
                  </h3>
                  <p className="font-body text-sm text-slate-400 line-clamp-2 mb-4">
                    {project.context}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(project.kpis || []).slice(0, 2).map((kpi, i) => (
                      <span
                        key={i}
                        className="font-body text-xs bg-blue-500/10 text-blue-400 px-3 py-1 rounded-full"
                      >
                        {kpi}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/projets"
            className="md:hidden flex items-center justify-center gap-2 text-blue-400 font-body text-sm font-medium mt-8"
          >
            Voir tous les projets <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <div className="section-divider" />

      {/* Latest Blog */}
      <section className="py-24 md:py-32" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-4">
                Actualites
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-slate-50">
                Derniers articles
              </h2>
            </div>
            <Link
              to="/blog"
              data-testid="see-all-blog"
              className="hidden md:inline-flex items-center gap-2 text-blue-400 font-body text-sm font-medium hover:text-blue-300 transition-colors"
            >
              Voir le blog <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group bg-slate-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 program-card-hover"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover img-zoom opacity-70 group-hover:opacity-90 transition-opacity"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="font-body text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full backdrop-blur-sm">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-medium text-slate-50 mb-2 group-hover:text-blue-400 transition-colors">
                    {post.title}
                  </h3>
                  <p className="font-body text-sm text-slate-400 line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Partners */}
      {partners.length > 0 && (
        <section className="py-24 md:py-32" data-testid="partners-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
            <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-4 text-center">
              Ils nous font confiance
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-slate-50 text-center">
              Nos Partenaires
            </h2>
          </div>
          <Marquee speed={40} gradient={false} className="py-8">
            {partners.concat(partners).map((partner, i) => (
              <div
                key={`${partner.id}-${i}`}
                className="mx-8 flex items-center justify-center min-w-[180px] h-16 bg-white/5 rounded-xl px-6 border border-white/10 hover:border-blue-500/30 transition-all"
              >
                <span className="font-body text-sm text-slate-400 font-medium whitespace-nowrap">
                  {partner.name}
                </span>
              </div>
            ))}
          </Marquee>
        </section>
      )}

      {/* Footer CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="footer-cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-50 mb-8">
            Rejoignez le mouvement pour une {"\u00e9"}ducation plus inclusive
          </h2>
          <p className="font-body text-base md:text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Chaque geste compte. Ensemble, r{"\u00e9"}duisons les in{"\u00e9"}galit{"\u00e9"}s d'acc{"\u00e8"}s {"\u00e0"} l'{"\u00e9"}ducation et aux technologies.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/soutenir"
              data-testid="footer-cta-donate"
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-full font-body font-medium transition-all duration-300"
            >
              Faire un don
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://www.linkedin.com/company/educpeople/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cta-linkedin"
              className="inline-flex items-center gap-2 bg-[#0A66C2] hover:bg-[#004182] text-white px-8 py-4 rounded-full font-body font-medium transition-all duration-300"
            >
              <Linkedin className="w-5 h-5" />
              Suivez-nous sur LinkedIn
            </a>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-full font-body font-medium transition-all duration-300"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
